import { useState, type ReactNode } from 'react';

/*
 * A heading and the rule under it are what separate one part of a page from the
 * next, now that nothing is boxed. The rule runs the full width of the content
 * rather than stopping at the text, which is what makes it read as the top edge
 * of a section instead of an underline on a title.
 */
export function PageHeading({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-6 border-b-2 border-rule-strong pb-3">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-1.5">{eyebrow}</p>
          <h1 className="text-[28px] leading-none font-semibold tracking-[-0.02em]">{title}</h1>
        </div>
        {children}
      </div>
    </div>
  );
}

/** The same device one level down, for a section within a page. */
export function SectionHeading({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <div className="mb-4 border-b border-rule pb-2.5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="eyebrow">{title}</p>
        {children}
      </div>
      {description ? <p className="mt-2 max-w-2xl text-sm text-ink-soft">{description}</p> : null}
    </div>
  );
}

/** An empty screen is an invitation to act, not a shrug. */
export function Empty({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="grid place-items-center border-b border-rule px-6 py-14 text-center">
      <p className="mb-1 font-semibold">{title}</p>
      <p className="max-w-sm text-sm text-ink-soft">{hint}</p>
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="rounded border px-3 py-2 text-sm"
      style={{
        borderColor: 'var(--color-absent)',
        background: 'var(--color-absent-soft)',
        color: 'var(--color-absent)',
      }}
    >
      {message}
    </p>
  );
}

/**
 * Says what just happened and offers to put it back.
 *
 * Removing a teacher, detaching a period, ending an enrolment and dropping a
 * meeting day each ask first (Confirm.tsx) and then offer this. The question
 * guards against a slip; this guards against a decision regretted a moment
 * later, and every one of the four is cheap to reverse because the caller
 * still holds the previous value.
 *
 * Not a toast. Nothing in this app floats except the register's mark menu and
 * what is built on Popover, and a message about one section belongs inside
 * that section rather than over the corner of the page, where it would be
 * missed on a long form.
 */
export function UndoNote({
  message,
  onUndo,
  onDismiss,
}: {
  message: string;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      className="panel flex flex-wrap items-center gap-3 px-3 py-2 text-sm text-ink-soft"
    >
      <span className="flex-1">{message}</span>
      <button type="button" className="btn" onClick={onUndo}>
        Undo
      </button>
      <button
        type="button"
        className="text-xs text-ink-faint underline"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        Dismiss
      </button>
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <p className="eyebrow py-10 text-center">{label}</p>;
}

/**
 * The masthead used by every page outside the shell: the login forms, the
 * password setup page, and the platform administrator's choice of
 * organization.
 */
export function Frame({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh place-items-center px-6 py-12">
      <div className="w-full max-w-[380px]">
        {/* The masthead of a mark book: ruled, stamped, unfussy. */}
        <div className="mb-8 border-b-2 border-ink pb-4">
          <p className="eyebrow mb-2">Quire · attendance</p>
          <h1 className="text-[32px] leading-none font-semibold tracking-[-0.03em]">{title}</h1>
          {subtitle ? <p className="mt-2 text-sm text-ink-soft">{subtitle}</p> : null}
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Shows a generated setup link once, for the administrator to send on.
 *
 * The link is displayed rather than emailed because this deployment has no mail
 * transport, and pretending otherwise would fail silently. It is shown in full
 * so it can be copied by hand when the clipboard API is unavailable - which
 * includes any deployment served over plain HTTP.
 */
export function SetupLinkPanel({
  url,
  expiresAt,
  onDismiss,
}: {
  url: string;
  expiresAt: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Insecure origins have no clipboard API. The text is on screen and
      // selectable, so the fallback is simply to let them select it.
      setCopied(false);
    }
  };

  return (
    <div className="panel mb-5 p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p className="eyebrow">Setup link</p>
        <button type="button" className="btn" onClick={onDismiss}>
          Done
        </button>
      </div>
      <p className="mb-3 max-w-2xl text-sm text-ink-soft">
        Send this to the user. It works once, and expires {new Date(expiresAt).toLocaleString()}.
        Nobody can see it again after you leave this page.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="field flex-1 font-mono text-xs"
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
        />
        <button type="button" className="btn btn-primary" onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
